import { InverterModelType } from 'ngx-core-services';
import {
  ModbusCommandOps,
  RegisterTable,
  V3AntiRefluxSettings,
  V3BatteryActiveSettings,
  V3BatterySettings,
  V3DeviceAntiRefluxAndPCCSettings,
  V3DeviceAutoTest,
  V3DevicePCCSettings,
  V3DeviceWorkModeConfig,
  V3DeviceWorkModePassive,
  V3DeviceWorkModeTOU,
  V3DeviceWorkModeTiming,
  V3EpsSettings,
  V3GlobalSettings,
  V3IVCurveScanSettings,
  V3InputModeChannels,
  V3ModbusSettings,
  V3ParallelSettings,
  V3PurchasePowerLimitSettings,
  V3SysTimeConfig,
  V3UnbalancedSupportSettings,
  V3StartUp,
  V3VoltageProtection,
  V3FrequencyProtection,
  V3DCIProtection,
  V3OverVoltageUnderVoltageDerate,
  V3ReactivePower,
  V3VRTConfig,
  V3OverFrequencyUnderFrequencyDerate,
  V3RemoteOnOffSettings,
  V3HeatingFilmSettings,
  V3CTDirectionSettings,
  V3ArcingAlarmSettings,
  V3IslandConfig,
} from 'ngx-modbus';

import {
  LeanFormSchema,
  CrossFieldTransformFn,
  DynamicOptionsFn,
  CustomTriggerHandlerFn,
  SelectOption,
} from '../types/config-form.types';
import { SafetyChartOutput } from '../utils/safety-chart-generators';
import {
  extractVoltageProtectionChart,
  extractFrequencyProtectionChart,
  extractDCIProtectionChart,
  extractActiveDerateChart,
  extractFreqDerateChart,
  extractReactivePowerChart,
  extractVRTChart,
} from '../utils/safety-chart-extractors';
import { WORK_MODE_FORM } from './work-mode.form';
import { BATTERY_CONFIG_FORM } from './battery-config.form';
import { LANGUAGE_FORM } from './language.form';
import { MODBUS_RS485_FORM } from './modbus-rs485.form';
import { IV_CURVE_SCAN_FORM } from './iv-curve-scan.form';
import { EPS_FORM } from './eps.form';
import { UNBALANCED_SUPPORT_FORM } from './unbalanced-support.form';
import { ANTI_REFLUX_FORM } from './anti-reflux.form';
import { PARALLEL_FORM } from './parallel.form';
import { PURCHASE_POWER_LIMIT_FORM } from './purchase-power-limit.form';
import { BATTERY_ACTIVE_FORM } from './battery-active.form';
import { SYS_TIME_FORM } from './sys-time.form';
import { INPUT_MODE_FORM } from './input-mode.form';
import {
  SAFETY_STARTUP_FORM,
  SAFETY_VOLTAGE_PROTECTION_FORM,
  SAFETY_FREQUENCY_PROTECTION_FORM,
  SAFETY_DCI_PROTECTION_FORM,
  SAFETY_REACTIVE_POWER_FORM,
  SAFETY_ACTIVE_DERATE_FORM,
  SAFETY_ACTIVE_POWER_LIMIT_FORM,
  SAFETY_DRM0_FORM,
  SAFETY_DRM1_8_FORM,
  SAFETY_VRT_FORM,
  SAFETY_OVER_FREQ_DERATE_FORM,
  SAFETY_ISLAND_FORM,
} from './safety.form';
import { REMOTE_ON_OFF_FORM } from './remote-on-off.form';
import { HEATING_FILM_FORM } from './heating-film.form';
import { CT_DIRECTION_FORM } from './ct-direction.form';
import { ARCING_ALARM_FORM } from './arcing-alarm.form';
import { AUTO_TEST_FORM } from './auto-test.form';

export type SectionGroup = 'energy' | 'grid' | 'system' | 'protection' | 'power' | 'autotest';
export type SectionTier = 'basic' | 'advanced';
export type SectionMode = 'short' | 'complete';

export interface SectionConfig {
  id: string;
  group: SectionGroup;
  /** Visibility tier: 'basic' sections are always shown, 'advanced' only in 'complete' mode. Default: 'basic'. */
  tier?: SectionTier;
  leanForm: LeanFormSchema;
  registerTables: Record<string, Partial<RegisterTable>>;
  readTable: Partial<RegisterTable>;
  readOptions?: ModbusCommandOps;
  saveLabel: string;
  showWhen?: (modelType: InverterModelType) => boolean;
  customTransforms?: Record<string, CrossFieldTransformFn>;
  customOptionGenerators?: Record<string, DynamicOptionsFn>;
  customTriggerHandlers?: Record<string, CustomTriggerHandlerFn>;
  labelMap?: Record<string, string>;
  dataTransform?: (data: Record<string, any>, context: { inverterId: string }) => Record<string, any>;
  /** Register key whose value is shown as summary in the closed accordion header */
  summaryRegisterKey?: string;
  /** Optional chart generator — called with section data to produce ECharts options.
   *  Returns null when chart should not be shown. */
  chartGenerator?: (data: Record<string, any>) => SafetyChartOutput | SafetyChartOutput[] | null;
}

const isHybridOrStorage = (t: InverterModelType) => t === InverterModelType.HybridOrStorage;

// ── SysTime: custom trigger handler ──

const sysTimeTriggerHandlers: Record<string, CustomTriggerHandlerFn> = {
  sysTimeUpdate: async ({ command, registerTables }) => {
    const now = new Date();
    await command.executeWriteMultipleHoldingRegisters([
      { key: 'SysTimeConfig_Year',    value: now.getFullYear() - 2000 },
      { key: 'SysTimeConfig_Month',   value: now.getMonth() + 1      },
      { key: 'SysTimeConfig_Date',    value: now.getDate()            },
      { key: 'SysTimeConfig_Hour',    value: now.getHours()           },
      { key: 'SysTimeConfig_Minute',  value: now.getMinutes()         },
      { key: 'SysTimeConfig_Second',  value: now.getSeconds()         },
      { key: 'SysTimeConfig_Control', value: 1                        },
    ], registerTables.V3SysTimeConfig);
  },
};

// ── Battery Config: custom transforms & option generators ──

const BATTERY_ADDRESS_NOT_USED = 32;
const BTS_5K_ADDRESS_OFFSET = 16;
const BTS_5K_PROTOCOLS = [11];

const batteryCustomTransforms: Record<string, CrossFieldTransformFn> = {
  batteryAddress: (value, direction, deps) => {
    if (direction === 'read') {
      if (value === BATTERY_ADDRESS_NOT_USED) {
        return BATTERY_ADDRESS_NOT_USED;
      }
      if (BTS_5K_PROTOCOLS.includes(deps.BAT_CONFIG_PROTOCOL)) {
        if (value === BTS_5K_ADDRESS_OFFSET) {
          return BATTERY_ADDRESS_NOT_USED;
        }
        if (deps.BAT_CONFIG_ID === 1) {
          const result = (value as number) - BTS_5K_ADDRESS_OFFSET;
          return result < 0 ? value : result;
        }
      }
      return value;
    }
    if (value === BATTERY_ADDRESS_NOT_USED) {
      return BATTERY_ADDRESS_NOT_USED;
    }
    if (deps.BAT_CONFIG_ID === 1 && BTS_5K_PROTOCOLS.includes(deps.BAT_CONFIG_PROTOCOL)) {
      return value + BTS_5K_ADDRESS_OFFSET;
    }
    return value;
  },
};

const batteryCustomOptions: Record<string, DynamicOptionsFn> = {
  batteryAddressOptions: (currentValue, deps, config) => {
    const protocolId = deps.BAT_CONFIG_PROTOCOL;
    const addressIndex = config?.addressIndex ?? 1;
    const options: SelectOption[] = [];

    if (BTS_5K_PROTOCOLS.includes(protocolId)) {
      options.push({ label: 'Not used', value: BATTERY_ADDRESS_NOT_USED });
    }

    if (addressIndex === 1) {
      if (currentValue !== 1 && currentValue !== 0 && currentValue !== BATTERY_ADDRESS_NOT_USED && typeof currentValue === 'number') {
        options.push({ label: `${currentValue}`, value: currentValue });
      }
      options.push({ label: '0', value: 0 }, { label: '1', value: 1 });
    } else {
      if (currentValue !== addressIndex && currentValue !== BATTERY_ADDRESS_NOT_USED && typeof currentValue === 'number') {
        options.push({ label: `${currentValue}`, value: currentValue });
      }
      options.push({ label: `${addressIndex}`, value: addressIndex });
    }

    return options;
  },
};

export const SECTION_CONFIGS: SectionConfig[] = [
  // ── Energy & Storage ──────────────────────────────
  {
    id: 'work-mode',

    group: 'energy',
    leanForm: WORK_MODE_FORM,
    registerTables: {
      V3DeviceWorkModeConfig,
      V3DeviceWorkModeTOU,
      V3DeviceWorkModeTiming,
      V3DeviceWorkModePassive,
    },
    readTable: V3DeviceWorkModeConfig,
    readOptions: {
      maxRegisterPerPage: 0x40,
      useValidityMask: 'AddressMask_Config_Remote1',
    },
    saveLabel: 'WorkMode',
    showWhen: isHybridOrStorage,
    summaryRegisterKey: 'WORK_MODE',
  },
  {
    id: 'battery-config',
group: 'energy',
    leanForm: BATTERY_CONFIG_FORM,
    registerTables: { V3BatterySettings },
    readTable: V3BatterySettings,
    readOptions: {
      maxRegisterPerPage: 0x20,
      useValidityMask: 'AddressMask_Config_Basic2',
    },
    saveLabel: 'BatteryConfig',
    showWhen: isHybridOrStorage,
    summaryRegisterKey: 'BAT_CONFIG_ID',
    customTransforms: batteryCustomTransforms,
    customOptionGenerators: batteryCustomOptions,
  },
  {
    id: 'battery-active',
    group: 'energy',
    leanForm: BATTERY_ACTIVE_FORM,
    registerTables: { V3BatteryActiveSettings },
    readTable: V3BatteryActiveSettings,
    saveLabel: 'BatteryActive',
    showWhen: isHybridOrStorage,
  },
  {
    id: 'purchase-power-limit',
    group: 'energy',
    leanForm: PURCHASE_POWER_LIMIT_FORM,
    registerTables: { V3PurchasePowerLimitSettings },
    readTable: V3PurchasePowerLimitSettings,
    readOptions: {
      optional: true,
    },
    saveLabel: 'PurchasePowerLimit',
    showWhen: isHybridOrStorage,
  },
  // ── Grid & Protection ─────────────────────────────
  {
    id: 'anti-reflux',
    group: 'grid',
    leanForm: ANTI_REFLUX_FORM,
    registerTables: { V3AntiRefluxSettings, V3DevicePCCSettings },
    readTable: V3DeviceAntiRefluxAndPCCSettings,
    saveLabel: 'AntiReflux',
  },
  {
    id: 'eps',
    group: 'grid',
    leanForm: EPS_FORM,
    registerTables: { V3EpsSettings },
    readTable: V3EpsSettings,
    saveLabel: 'EPS',
    showWhen: isHybridOrStorage,
    summaryRegisterKey: 'EPS_Control',
  },
  {
    id: 'unbalanced-support',
    group: 'grid',
    leanForm: UNBALANCED_SUPPORT_FORM,
    registerTables: { V3UnbalancedSupportSettings },
    readTable: V3UnbalancedSupportSettings,
    saveLabel: 'UnbalancedSupport',
    showWhen: isHybridOrStorage,
  },
  {
    id: 'parallel',
    group: 'grid',
    leanForm: PARALLEL_FORM,
    registerTables: { V3ParallelSettings },
    readTable: V3ParallelSettings,
    saveLabel: 'Parallel',
    showWhen: isHybridOrStorage,
  },
  // ── System ────────────────────────────────────────
  {
    id: 'language',
    group: 'system',
    leanForm: LANGUAGE_FORM,
    registerTables: { V3GlobalSettings },
    readTable: V3GlobalSettings,
    saveLabel: 'Language',
    summaryRegisterKey: 'LANG',
  },
  {
    id: 'modbus-rs485',
    group: 'system',
    leanForm: MODBUS_RS485_FORM,
    registerTables: { V3ModbusSettings },
    readTable: V3ModbusSettings,
    saveLabel: 'ModbusRS485',
  },
  {
    id: 'iv-curve-scan',
    group: 'system',
    leanForm: IV_CURVE_SCAN_FORM,
    registerTables: { V3IVCurveScanSettings },
    readTable: V3IVCurveScanSettings,
    saveLabel: 'IVCurveScan',
  },
  {
    id: 'sys-time',
    group: 'system',
    leanForm: SYS_TIME_FORM,
    registerTables: { V3SysTimeConfig },
    readTable: V3SysTimeConfig,
    saveLabel: 'SysTime',
    customTriggerHandlers: sysTimeTriggerHandlers,
  },
  {
    id: 'input-mode',
    group: 'system',
    leanForm: INPUT_MODE_FORM,
    registerTables: { V3InputModeChannels },
    readTable: V3InputModeChannels,
    readOptions: { maxRegisterPerPage: 0x20 },
    saveLabel: 'InputMode',
    dataTransform: (data, { inverterId }) => ({ ...data, SN: inverterId }),
  },
  // ── Safety / Grid Network ──────────────────────
  {
    id: 'safety-startup',
    group: 'protection',
    tier: 'advanced',
    leanForm: SAFETY_STARTUP_FORM,
    registerTables: { V3StartUp },
    readTable: V3StartUp,
    saveLabel: 'SafetyStartup',
  },
  {
    id: 'safety-voltage-protection',
    group: 'protection',
    tier: 'advanced',
    leanForm: SAFETY_VOLTAGE_PROTECTION_FORM,
    registerTables: { V3VoltageProtection },
    readTable: V3VoltageProtection,
    saveLabel: 'SafetyVoltage',
    chartGenerator: extractVoltageProtectionChart,
  },
  {
    id: 'safety-frequency-protection',
    group: 'protection',
    tier: 'advanced',
    leanForm: SAFETY_FREQUENCY_PROTECTION_FORM,
    registerTables: { V3FrequencyProtection },
    readTable: V3FrequencyProtection,
    saveLabel: 'SafetyFrequency',
    chartGenerator: extractFrequencyProtectionChart,
  },
  {
    id: 'safety-dci-protection',
    group: 'protection',
    tier: 'advanced',
    leanForm: SAFETY_DCI_PROTECTION_FORM,
    registerTables: { V3DCIProtection },
    readTable: V3DCIProtection,
    saveLabel: 'SafetyDCI',
    chartGenerator: extractDCIProtectionChart,
  },
  // ── Power Management ───────────────────────────
  {
    id: 'safety-reactive-power',
    group: 'power',
    tier: 'advanced',
    leanForm: SAFETY_REACTIVE_POWER_FORM,
    registerTables: { V3ReactivePower },
    readTable: V3ReactivePower,
    saveLabel: 'SafetyReactivePower',
    chartGenerator: extractReactivePowerChart,
  },
  {
    id: 'safety-active-derate',
    group: 'power',
    tier: 'advanced',
    leanForm: SAFETY_ACTIVE_DERATE_FORM,
    registerTables: { V3OverVoltageUnderVoltageDerate },
    readTable: V3OverVoltageUnderVoltageDerate,
    saveLabel: 'SafetyActiveDerate',
    chartGenerator: extractActiveDerateChart,
  },
  {
    id: 'safety-active-power-limit',
    group: 'power',
    tier: 'advanced',
    leanForm: SAFETY_ACTIVE_POWER_LIMIT_FORM,
    registerTables: { V3OverVoltageUnderVoltageDerate },
    readTable: V3OverVoltageUnderVoltageDerate,
    saveLabel: 'SafetyActivePowerLimit',
  },
  {
    id: 'safety-drm0',
    group: 'power',
    tier: 'advanced',
    leanForm: SAFETY_DRM0_FORM,
    registerTables: { V3OverVoltageUnderVoltageDerate },
    readTable: V3OverVoltageUnderVoltageDerate,
    saveLabel: 'SafetyDRM0',
  },
  {
    id: 'safety-drm1-8',
    group: 'power',
    tier: 'advanced',
    leanForm: SAFETY_DRM1_8_FORM,
    registerTables: { V3OverVoltageUnderVoltageDerate },
    readTable: V3OverVoltageUnderVoltageDerate,
    saveLabel: 'SafetyDRM1_8',
  },
  {
    id: 'safety-vrt',
    group: 'power',
    tier: 'advanced',
    leanForm: SAFETY_VRT_FORM,
    registerTables: { V3VRTConfig },
    readTable: V3VRTConfig,
    saveLabel: 'SafetyVRT',
    chartGenerator: extractVRTChart,
  },
  {
    id: 'safety-over-freq-derate',
    group: 'power',
    tier: 'advanced',
    leanForm: SAFETY_OVER_FREQ_DERATE_FORM,
    registerTables: { V3OverFrequencyUnderFrequencyDerate },
    readTable: V3OverFrequencyUnderFrequencyDerate,
    saveLabel: 'SafetyOverFreqDerate',
    chartGenerator: extractFreqDerateChart,
  },
  {
    id: 'remote-on-off',
    group: 'power',
    tier: 'advanced',
    leanForm: REMOTE_ON_OFF_FORM,
    registerTables: { V3RemoteOnOffSettings },
    readTable: V3RemoteOnOffSettings,
    readOptions: { useValidityMask: 'AddressMask_Config_Remote1' },
    saveLabel: 'RemoteOnOff',
  },
  {
    id: 'safety-island',
    group: 'protection',
    tier: 'advanced',
    leanForm: SAFETY_ISLAND_FORM,
    registerTables: { V3IslandConfig },
    readTable: V3IslandConfig,
    saveLabel: 'SafetyIsland',
  },
  // ── Protection extras ──────────────────────────
  {
    id: 'heating-film',
    group: 'protection',
    tier: 'advanced',
    leanForm: HEATING_FILM_FORM,
    registerTables: { V3HeatingFilmSettings },
    readTable: V3HeatingFilmSettings,
    readOptions: { useValidityMask: 'AddressMask_Config_Basic1' },
    saveLabel: 'HeatingFilm',
  },
  {
    id: 'ct-direction',
    group: 'protection',
    tier: 'advanced',
    leanForm: CT_DIRECTION_FORM,
    registerTables: { V3CTDirectionSettings },
    readTable: V3CTDirectionSettings,
    readOptions: { useValidityMask: 'AddressMask_Config_Basic2' },
    saveLabel: 'CTDirection',
  },
  {
    id: 'arcing-alarm',
    group: 'protection',
    tier: 'advanced',
    leanForm: ARCING_ALARM_FORM,
    registerTables: { V3ArcingAlarmSettings },
    readTable: V3ArcingAlarmSettings,
    readOptions: { useValidityMask: 'AddressMask_Config_Basic3' },
    saveLabel: 'ArcingAlarm',
  },
  // ── Auto Test (CEI 021) ─────────────────────────
  {
    id: 'auto-test',
    group: 'autotest',
    tier: 'advanced',
    leanForm: AUTO_TEST_FORM,
    registerTables: { V3DeviceAutoTest },
    readTable: V3DeviceAutoTest,
    readOptions: { maxRegisterPerPage: 0x40 },
    saveLabel: 'AutoTest',
  },
];

/** Filter sections by mode: 'short' shows only basic, 'complete' shows all */
export const filterSectionsByMode = (configs: SectionConfig[], mode: SectionMode): SectionConfig[] =>
  mode === 'complete' ? configs : configs.filter(c => c.tier !== 'advanced');
